import { activityIcon, activitySearchLabel } from './activityPresentation'
import { Fragment, ReactNode, useEffect, useState } from 'react'

import { ChevronRight, type IconComponent } from '@digital-twins/design-system/icons'

import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  getThreadPlaceholders,
  isCountedActivityUnit,
  resolveThreadLanguage,
  ThreadPlaceholderTemplates,
  ThreadPluralTemplates,
} from '@/lib/constants/languages'
import { cn } from '@/lib/utils'

import {
  clipActivityQuery,
  ThreadActivityLeg,
} from './ThreadActivityIndicator'
import {
  ActivityObject,
  ActivityPageData,
  resolveActivityObject,
} from './resolveActivityObject'
import { ThreadStepGlyph, ThreadStepGlyphState } from './ThreadStepGlyph'

/**
 * The activity checklist: one row per step a turn actually ran, in start
 * order (docs/plans/thread-activity-steps.md, Steps 3 and 4).
 *
 * Every row comes from a real `tool_start` / `tool_end` pair (D1). Nothing is
 * planned ahead, so there are only running and finished rows, never pending
 * ones. A row is the localized verb, the object the page can name (D5) or the
 * model's quoted lookup (F12), and an outcome: a count for searches only (D4),
 * "nothing relevant" for an empty search. A step that failed, or that the stream
 * never closed, is left out: the saved status stays on the row data, and the
 * answer says what could not be read.
 *
 * Copy follows the THREAD's language through `THREAD_PLACEHOLDERS[...].steps`,
 * never the app's i18n files, the same as the waiting line it replaces.
 */

/** Visible rows before "+N more" (F14). */
export const ACTIVITY_STEPS_VISIBLE = 6

/**
 * Tools that can run long enough for a reader to wonder (image plan, Step 4b):
 * their running row shows live elapsed seconds.
 */
export const LONG_RUNNING_TOOLS: ReadonlySet<string> = new Set(['generate_image'])

/** Elapsed seconds are shown from this many on, so a quick call never flickers. */
const ELAPSED_VISIBLE_FROM_SECONDS = 1

/** Locale seconds ("12s", "12 s") through `Intl`, no copy table needed. */
export function formatElapsedSeconds(seconds: number, language: string): string {
  try {
    return new Intl.NumberFormat(language, {
      style: 'unit',
      unit: 'second',
      unitDisplay: 'narrow',
    }).format(seconds)
  } catch {
    return `${seconds}s`
  }
}

/** Whole seconds since this hook started counting; 0 while inactive. */
function useElapsedSeconds(isActive: boolean): number {
  const [seconds, setSeconds] = useState(0)

  useEffect(() => {
    if (!isActive) {
      setSeconds(0)
      return
    }
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [isActive])

  return seconds
}

/**
 * Search steps are the only ones whose `itemCount` is a result count (D4). The
 * web search prints none (A11 D7): it reads "Searched the web" with no number.
 */
const COUNTED_TOOLS = new Set(['document_search', 'research_search'])

export interface ThreadActivityStepView {
  tool: string
  icon: IconComponent
  state: ThreadStepGlyphState
  /** The localized verb phrase, without the object. */
  step: string
  object: ActivityObject | null
  /** The object as it prints (quoted for quote and search kinds, clipped). */
  objectLabel: string | null
  /** The full object text when `objectLabel` was clipped, for the tooltip. */
  objectFull: string | null
  /** The localized template the row fills (`{step}` alone, or with a slot). */
  template: string
  outcome: string | null
  /** The whole row as one string: the live region and the accessible name. */
  text: string
}

function hasKey<T>(values: Record<string, T>, key: string): boolean {
  return Object.hasOwn(values, key)
}

/**
 * Plural-safe count copy through `Intl.PluralRules`, falling back to `other`
 * for a category the language does not list.
 */
export function formatActivityPlural(
  templates: ThreadPluralTemplates,
  count: number,
  language: string,
): string {
  let category: Intl.LDMLPluralRule = 'other'
  try {
    category = new Intl.PluralRules(language).select(count)
  } catch {
    category = 'other'
  }

  let formatted = String(count)
  try {
    formatted = new Intl.NumberFormat(language).format(count)
  } catch {
    formatted = String(count)
  }

  const template = templates[category] ?? templates.other
  return template.replace('{count}', formatted)
}

function fillText(template: string, slots: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.hasOwn(slots, key) ? slots[key] : match,
  )
}

function fillNodes(
  template: string,
  slots: Record<string, ReactNode>,
): Array<ReactNode> {
  return template.split(/(\{\w+\})/g).map((part, index) => {
    const key = part.startsWith('{') && part.endsWith('}') ? part.slice(1, -1) : null
    if (key && Object.hasOwn(slots, key)) {
      return <Fragment key={index}>{slots[key]}</Fragment>
    }
    return part ? <Fragment key={index}>{part}</Fragment> : null
  })
}

/**
 * A leg the stream never closed is not a finished step: once the stream is
 * over (an error, a stop, or a producer that dropped its `tool_end`) it reads
 * as unavailable, never as a checked row.
 */
function isLegUnavailable(leg: ThreadActivityLeg, settled: boolean): boolean {
  if (leg.done) {
    return leg.ok === false
  }
  return settled
}

/** The steps the list shows. */
function shownLegs(legs: Array<ThreadActivityLeg>, settled: boolean): Array<ThreadActivityLeg> {
  return legs.filter((leg) => !isLegUnavailable(leg, settled))
}

function legState(leg: ThreadActivityLeg, settled: boolean): ThreadStepGlyphState {
  if (isLegUnavailable(leg, settled)) {
    return 'unavailable'
  }
  if (leg.done) {
    return 'done'
  }
  return 'active'
}

function legOutcome(params: {
  leg: ThreadActivityLeg
  state: ThreadStepGlyphState
  copy: ThreadPlaceholderTemplates
  language: string
}): string | null {
  const { leg, state, copy, language } = params

  if (leg.done && leg.sourceResults?.length) {
    // An empty source is not news: the label already names where it looked.
    const parts = leg.sourceResults.flatMap(result => {
      if (result.outcome === 'unavailable' || result.outcome === 'empty') return []
      if (!isCountedActivityUnit(result.unit)) return []
      return [formatActivityPlural(copy.steps.resultCounts[result.unit], result.count!, language)]
    })
    if (parts.length) {
      return parts.join(' · ')
    }
    return leg.sourceResults.every(result => result.outcome === 'empty') ? copy.steps.nothingRelevant : null
  }
  if (state !== 'done' || !leg.done || !COUNTED_TOOLS.has(leg.tool)) {
    return null
  }
  if (typeof leg.itemCount !== 'number') {
    return null
  }
  if (leg.itemCount <= 0) {
    return copy.steps.nothingRelevant
  }
  return formatActivityPlural(copy.steps.results, leg.itemCount, language)
}

/**
 * One row's words. Pure and exported so the copy rules are testable without
 * a render. A tool this build has no copy for still returns a row, worded as
 * the generic `unknownRunning` / `unknownDone` pair, so a new wire tool never
 * prints its raw name.
 */
export function describeActivityStep(params: {
  leg: ThreadActivityLeg
  copy: ThreadPlaceholderTemplates
  /** Already resolved through `resolveThreadLanguage`. */
  language: string
  pageData?: ActivityPageData | null
  /** The stream is over: an open leg is shown as unavailable, not running. */
  settled?: boolean
}): ThreadActivityStepView {
  const { leg, copy, language, pageData, settled = false } = params

  const state = legState(leg, settled)
  const isRunning = state === 'active'
  /* A leg that never closed did not finish, so it keeps the running verb
     (without the ellipsis) beside its "unavailable" outcome. */
  const isPresentTense = !leg.done
  const known = hasKey(copy.steps.done, leg.tool)
  const verbs = isPresentTense ? copy.steps.running : copy.steps.done
  let step = isPresentTense ? copy.steps.unknownRunning : copy.steps.unknownDone
  if (known) {
    step = verbs[leg.tool as keyof typeof verbs]
  }

  step = activitySearchLabel(leg, copy, language) ?? step
  const object: ActivityObject | null = leg.subject?.name
    ? { kind: 'name', text: leg.subject.name }
    : known ? resolveActivityObject(leg.tool, leg.query, pageData) : null
  const social = copy.steps.social
  const socialDone = social?.done[leg.tool as keyof typeof social.done]
  const isSocial = Boolean(socialDone)
  if (social && isSocial && isRunning && !object) {
    step = social.runningUnnamed
  }
  const clipped = object ? clipActivityQuery(object.text) : null
  const quoted = (text: string) => fillText(copy.steps.quote, { text })
  const objectLabel =
    object && clipped ? (object.kind === 'name' ? clipped : quoted(clipped)) : null
  const objectFull =
    object && clipped && clipped !== object.text ? object.text : null

  let template = '{step}'
  if (object && objectLabel) {
    template = object.kind === 'search' ? copy.steps.withQuery : copy.steps.withObject
    if (social && socialDone && object.kind === 'name') {
      template = isRunning ? social.running : socialDone
    }
  }

  const outcome = legOutcome({ leg, state, copy, language })
  const ellipsis = isRunning ? '…' : ''
  const sentence = fillText(template, {
    step,
    object: objectLabel ?? '',
    query: objectLabel ?? '',
  })

  return {
    tool: leg.tool,
    icon: activityIcon(leg),
    state,
    step,
    object,
    objectLabel,
    objectFull,
    template,
    outcome,
    text: `${sentence}${ellipsis}${outcome ? ` · ${outcome}` : ''}`,
  }
}

/**
 * What the indicator's single hidden live region speaks while the checklist
 * is live: the newest row, or `composing` once every step has reported (F11).
 */
export function activityLiveText(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
  language: string
  pageData?: ActivityPageData | null
}): string {
  const { legs, copy, language, pageData } = params

  if (legs.length > 0 && legs.every((leg) => leg.done)) {
    return copy.composing
  }
  const newest = shownLegs(legs, false).at(-1)
  if (!newest) {
    return copy.working
  }
  return describeActivityStep({ leg: newest, copy, language, pageData }).text
}

/**
 * The folded line under a finished trail (D8): how many steps ran. Never
 * "sources": saving an artifact is not one (F7).
 */
export function activityTrailSummary(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
  language: string
  /** False while the answer still streams: an open leg may yet close. */
  settled?: boolean
}): string {
  const { legs, copy, language, settled = true } = params

  return formatActivityPlural(copy.steps.summary, shownLegs(legs, settled).length, language)
}

function StepIcon({ tool, icon: Icon }: { tool: string; icon: IconComponent }) {
  return <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden data-testid={`thread-activity-step-icon-${tool}`} />
}

interface ThreadActivityStepRowProps {
  view: ThreadActivityStepView
  index: number
  language: string
}

function ThreadActivityStepRow(props: ThreadActivityStepRowProps) {
  const { view, index, language } = props

  const isTimed = view.state === 'active' && LONG_RUNNING_TOOLS.has(view.tool)
  const elapsedSeconds = useElapsedSeconds(isTimed)

  const objectNode = view.objectLabel ? (
    view.objectFull ? (
      <Tooltip>
        <TooltipTrigger
          className="inline border-0 bg-transparent p-0 text-left text-foreground"
          data-testid={`thread-activity-step-object-${index}`}
        >
          {view.objectLabel}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs"
          data-testid={`thread-activity-step-object-full-${index}`}
        >
          <Text size="xs">{view.objectFull}</Text>
        </TooltipContent>
      </Tooltip>
    ) : (
      <span
        className="text-foreground"
        data-testid={`thread-activity-step-object-${index}`}
      >
        {view.objectLabel}
      </span>
    )
  ) : null

  return (
    <li
      className="relative z-[1] flex items-start gap-2.5"
      data-testid={`thread-activity-step-${index}`}
      data-state={view.state}
      data-tool={view.tool}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center bg-background">
        <ThreadStepGlyph state={view.state} testIdPrefix="thread-activity" />
      </span>
      <StepIcon tool={view.tool} icon={view.icon} />
      <Text as="span" size="sm" color="muted" className="min-w-0 break-words">
        {fillNodes(view.template, {
          step: view.step,
          object: objectNode,
          query: objectNode,
        })}
        {view.state === 'active' ? '…' : null}
        {isTimed && elapsedSeconds >= ELAPSED_VISIBLE_FROM_SECONDS ? (
          <span data-testid={`thread-activity-step-elapsed-${index}`}>
            {` · ${formatElapsedSeconds(elapsedSeconds, language)}`}
          </span>
        ) : null}
        {view.outcome ? (
          <span data-testid={`thread-activity-step-outcome-${index}`}>
            {` · ${view.outcome}`}
          </span>
        ) : null}
      </Text>
    </li>
  )
}

interface ThreadActivityStepsProps {
  legs: Array<ThreadActivityLeg>
  /** The thread's answer language. Null or absent falls back to English. */
  language?: string | null
  pageData?: ActivityPageData | null
  /**
   * Live: the turn is still waiting for its first token. Running rows show
   * the loader, a trailing `composing` row appears once every step has
   * reported, and the newest rows stay in view. The live region is the
   * indicator's, not this list's (`activityLiveText`, F11). Otherwise the
   * trail is settled history.
   */
  isLive?: boolean
  /** Overrides settled history while a folded trail's answer still streams. */
  settled?: boolean
}

export function ThreadActivitySteps(props: ThreadActivityStepsProps) {
  const { legs, language, pageData, isLive = false, settled } = props

  const [isShowingAll, setIsShowingAll] = useState(false)

  const resolved = resolveThreadLanguage(language)
  const copy = getThreadPlaceholders(resolved)
  const isSettled = settled ?? !isLive
  const shown = shownLegs(legs, isSettled)
  const views = shown.map((leg) =>
    describeActivityStep({
      leg,
      copy,
      language: resolved,
      pageData,
      settled: isSettled,
    }),
  )
  const isComposing = isLive && legs.length > 0 && legs.every((leg) => leg.done)
  const hidden = Math.max(0, views.length - ACTIVITY_STEPS_VISIBLE)

  /* Live keeps the NEWEST rows in view, since the running step is what the
     reader is waiting on. History keeps the first rows, and "+N more" opens
     the rest. */
  const firstVisible = isLive ? hidden : 0
  const visible =
    isShowingAll || hidden === 0
      ? views.map((view, index) => ({ view, index }))
      : views
          .map((view, index) => ({ view, index }))
          .slice(firstVisible, firstVisible + ACTIVITY_STEPS_VISIBLE)
  const moreLabel = copy.steps.more.replace('{count}', String(hidden))

  const moreLive =
    isLive && hidden > 0 ? (
      <Text
        size="xs"
        color="muted"
        className="pl-[26px]"
        data-testid="thread-activity-steps-more"
      >
        {moreLabel}
      </Text>
    ) : null

  const moreHistory =
    !isLive && hidden > 0 && !isShowingAll ? (
      <button
        type="button"
        className="ml-[26px] w-fit border-0 bg-transparent p-0 text-left text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setIsShowingAll(true)}
        data-testid="thread-activity-steps-more"
      >
        {moreLabel}
      </button>
    ) : null

  const composingRow = isComposing ? (
    <li
      className="relative z-[1] flex items-start gap-2.5"
      data-testid="thread-activity-step-composing"
      data-state="active"
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center bg-background">
        <ThreadStepGlyph state="active" testIdPrefix="thread-activity" />
      </span>
      <Text as="span" size="sm" color="muted">
        {copy.composing}
      </Text>
    </li>
  ) : null

  return (
    <div className="flex flex-col gap-2" data-testid="thread-activity-steps">
      {moreLive}
      <TooltipProvider delayDuration={150}>
        <ol
          className="space-y-2"
          aria-label={formatActivityPlural(copy.steps.summary, shown.length, resolved)}
        >
          {visible.map(({ view, index }) => (
            <ThreadActivityStepRow key={index} view={view} index={index} language={resolved} />
          ))}
          {composingRow}
        </ol>
      </TooltipProvider>
      {moreHistory}
    </div>
  )
}

interface ThreadActivityTrailProps {
  legs: Array<ThreadActivityLeg> | undefined
  language?: string | null
  pageData?: ActivityPageData | null
  /** True while the answer streams: an open leg is still running, not stopped. */
  isStreaming?: boolean
}

/**
 * The folded trail above an answer (Step 4): one line that keeps a single
 * row's height so the answer moves little when it folds (F15), and expands to
 * the finished rows. Shown live from the first token and after a reload from
 * the saved `toolCalls`. A turn that ran no steps shows nothing.
 */
export function ThreadActivityTrail(props: ThreadActivityTrailProps) {
  const { legs, language, pageData, isStreaming = false } = props

  const [isExpanded, setIsExpanded] = useState(false)

  if (!legs || legs.length === 0) {
    return null
  }

  if (shownLegs(legs, !isStreaming).length === 0) {
    return null
  }

  const resolved = resolveThreadLanguage(language)
  const copy = getThreadPlaceholders(resolved)
  const summary = activityTrailSummary({ legs, copy, language: resolved, settled: !isStreaming })

  const expanded = isExpanded ? (
    <div className="pt-1" data-testid="thread-activity-trail-steps">
      <ThreadActivitySteps
        legs={legs}
        language={language}
        pageData={pageData}
        settled={!isStreaming}
      />
    </div>
  ) : null

  return (
    <div className="flex flex-col gap-1.5" data-testid="thread-activity-trail">
      <button
        type="button"
        className="inline-flex w-fit items-center gap-1 border-0 bg-transparent p-0 text-left text-sm text-muted-foreground hover:text-foreground"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((value) => !value)}
        data-testid="thread-activity-trail-toggle"
      >
        <span data-testid="thread-activity-trail-summary">{summary}</span>
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 transition-transform motion-reduce:transition-none',
            isExpanded && 'rotate-90',
          )}
          aria-hidden
        />
      </button>
      {expanded}
    </div>
  )
}
